import type { AssistantConfirmationRequest, ConfirmAnswer } from '@/stores/assistant-confirm-store'

/** Ask the user and resolve with their answer. Injected so this module never imports React or the store. */
export type ConfirmFn = (request: AssistantConfirmationRequest) => Promise<ConfirmAnswer>

/**
 * Narrow the model's `confirmation` argument STRICTLY.
 *
 * No coercion, deliberately. The server wrote every value as a string and later
 * compares the copy field-for-field, so a `confirmation` whose values are no
 * longer strings has already been altered — accepting it here would show the
 * user a question and then refuse the action as tampered AFTER they answered.
 * Refusing up front gives the model an error it can act on ("copy it verbatim")
 * instead of a dead end.
 */
export function readConfirmationRequest(input: unknown): AssistantConfirmationRequest | null {
  const raw = (input ?? {}) as { confirmation?: unknown }
  const c = raw.confirmation

  if (!c || typeof c !== 'object') return null

  const v = c as Record<string, unknown>
  const strings = ['confirmationId', 'action', 'subject', 'title'] as const

  if (strings.some(k => typeof v[k] !== 'string' || v[k] === '')) return null
  if (!v.details || typeof v.details !== 'object') return null

  const details: Record<string, string> = {}

  for (const [k, value] of Object.entries(v.details as Record<string, unknown>)) {
    if (typeof value !== 'string') return null
    details[k] = value
  }

  return {
    confirmationId: v.confirmationId as string,
    action: v.action as string,
    subject: v.subject as string,
    title: v.title as string,
    details,
  }
}

/**
 * `request_user_confirmation`'s browser half — the one client tool that does not
 * resolve immediately.
 *
 * With no `confirm` dep there is no UI to ask with, so it answers NOT CONFIRMED
 * and says so. It never resolves as approved, and it never throws: a throw here
 * would suspend the server's run forever.
 */
export async function executeRequestUserConfirmation(input: unknown, confirm?: ConfirmFn): Promise<unknown> {
  const request = readConfirmationRequest(input)

  if (!request) {
    return {
      confirmed: false,
      error:
        '`confirmation` must be the object from the tool result, copied verbatim: confirmationId, action, ' +
        'subject, title and details, all as strings. Nothing was shown to the user.',
    }
  }

  if (!confirm) {
    return {
      confirmed: false,
      unavailable: true,
      error: 'This surface cannot show a confirmation prompt, so nothing was confirmed and nothing was changed.',
    }
  }

  const answer = await confirm(request)

  // `confirmationId` travels back so the SERVER can tie the answer to one
  // action — `readConfirmation` looks the decision up by that id. `reason` is
  // diagnostics the model relays ("you closed the panel", "it timed out"); the
  // gate reads `confirmed` and nothing else.
  return {
    confirmed: answer.confirmed,
    confirmationId: request.confirmationId,
    ...(answer.reason ? { reason: answer.reason } : {}),
  }
}
